/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { useContext } from 'react';
import PropTypes from "prop-types";
import { Droppable } from 'react-beautiful-dnd';
import classNames from 'classnames';
import { EuiDragDropContextContext } from './drag_drop_context';
var spacingToClassNameMap = {
  none: null,
  s: 'euiDroppable--s',
  m: 'euiDroppable--m',
  l: 'euiDroppable--l'
};
export var EuiDroppableContext = React.createContext({
  cloneItems: false
});
export var EuiDroppable = ({
  droppableId,
  direction,
  isDropDisabled = false,
  children,
  className,
  cloneDraggables = false,
  spacing = 'none',
  style,
  type = 'EUI_DEFAULT',
  withPanel = false,
  grow = false,
  'data-test-subj': dataTestSubj = 'droppable',
  ...rest
}) => {
  const {
    isDraggingType
  } = useContext(EuiDragDropContextContext);
  const dropIsDisabled = cloneDraggables ? true : isDropDisabled;
  return <Droppable isDropDisabled={dropIsDisabled} droppableId={droppableId} direction={direction} type={type} {...rest}>
      {(provided, snapshot) => {
      const classes = classNames('euiDroppable', {
        'euiDroppable--isDisabled': dropIsDisabled,
        'euiDroppable--isDraggingOver': snapshot.isDraggingOver,
        'euiDroppable--isDraggingType': isDraggingType === type,
        'euiDroppable--withPanel': withPanel,
        'euiDroppable--grow': grow,
        'euiDroppable--noGrow': !grow
      }, spacingToClassNameMap[spacing], className);
      const placeholderClasses = classNames('euiDroppable__placeholder', {
        'euiDroppable__placeholder--isHidden': cloneDraggables
      });
      const DroppableElement = typeof children === 'function' ? children(provided, snapshot) : children;
      return <div {...provided.droppableProps} ref={provided.innerRef} style={style} data-test-subj={dataTestSubj} className={classes}>
            <EuiDroppableContext.Provider value={{
          cloneItems: cloneDraggables
        }}>
              {DroppableElement}
            </EuiDroppableContext.Provider>
            <div className={placeholderClasses}>{provided.placeholder}</div>
          </div>;
    }}
    </Droppable>;
};
EuiDroppable.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element.isRequired, PropTypes.arrayOf(PropTypes.element.isRequired).isRequired, PropTypes.any.isRequired]).isRequired,
  className: PropTypes.string,

  /**
     * Makes its items immutable. Dragging creates cloned items that can be dropped elsewhere.
     */
  cloneDraggables: PropTypes.bool,
  style: PropTypes.any,

  /**
     * Adds padding to the droppable area
     */
  spacing: PropTypes.oneOf(["none", "s", "m", "l"]),

  /**
     * Adds an EuiPanel style to the droppable area
     */
  withPanel: PropTypes.bool,

  /**
     * Allow the panel to flex-grow?
     */
  grow: PropTypes.bool,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};

try {
  // @ts-ignore
  EuiDroppable.displayName = "EuiDroppable"; // @ts-ignore

  EuiDroppable.__docgenInfo = {
    "description": "",
    "displayName": "EuiDroppable",
    "props": {
      "className": {
        "defaultValue": null,
        "description": "",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "cloneDraggables": {
        "defaultValue": {
          value: false
        },
        "description": "Makes its items immutable. Dragging creates cloned items that can be dropped elsewhere.",
        "name": "cloneDraggables",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "style": {
        "defaultValue": null,
        "description": "",
        "name": "style",
        "required": false,
        "type": {
          "name": "CSSProperties | undefined"
        }
      },
      "spacing": {
        "defaultValue": {
          value: "none"
        },
        "description": "Adds padding to the droppable area",
        "name": "spacing",
        "required": false,
        "type": {
          "name": "\"none\" | \"s\" | \"m\" | \"l\" | undefined"
        }
      },
      "withPanel": {
        "defaultValue": {
          value: false
        },
        "description": "Adds an EuiPanel style to the droppable area",
        "name": "withPanel",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "grow": {
        "defaultValue": {
          value: false
        },
        "description": "Allow the panel to flex-grow?",
        "name": "grow",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "aria-label": {
        "defaultValue": null,
        "description": "",
        "name": "aria-label",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "data-test-subj": {
        "defaultValue": null,
        "description": "",
        "name": "data-test-subj",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "type": {
        "defaultValue": {
          value: "EUI_DEFAULT"
        },
        "description": "",
        "name": "type",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "direction": {
        "defaultValue": null,
        "description": "",
        "name": "direction",
        "required": false,
        "type": {
          "name": "\"horizontal\" | \"vertical\" | undefined"
        }
      },
      "mode": {
        "defaultValue": null,
        "description": "",
        "name": "mode",
        "required": false,
        "type": {
          "name": "\"standard\" | \"virtual\" | undefined"
        }
      },
      "droppableId": {
        "defaultValue": null,
        "description": "",
        "name": "droppableId",
        "required": true,
        "type": {
          "name": "string"
        }
      },
      "isDropDisabled": {
        "defaultValue": {
          value: false
        },
        "description": "",
        "name": "isDropDisabled",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "isCombineEnabled": {
        "defaultValue": null,
        "description": "",
        "name": "isCombineEnabled",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "ignoreContainerClipping": {
        "defaultValue": null,
        "description": "",
        "name": "ignoreContainerClipping",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "renderClone": {
        "defaultValue": null,
        "description": "",
        "name": "renderClone",
        "required": false,
        "type": {
          "name": "DraggableChildrenFn | undefined"
        }
      },
      "getContainerForClone": {
        "defaultValue": null,
        "description": "",
        "name": "getContainerForClone",
        "required": false,
        "type": {
          "name": "(() => ReactElement<HTMLElement, string | ((props: any) => ReactElement<any, string | ... | (new (props: any) => Component<any, any, any>)> | null) | (new (props: any) => Component<...>)>) | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/drag_and_drop/droppable.tsx#EuiDroppable"] = {
      docgenInfo: EuiDroppable.__docgenInfo,
      name: "EuiDroppable",
      path: "src/components/drag_and_drop/droppable.tsx#EuiDroppable"
    };
} catch (__react_docgen_typescript_loader_error) {}